import React, { useState, useRef, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import '../App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MEMBERSHIP_TIERS = [
  { value: '7k_basic_signup_promotion', label: '7k Basic Signup Promotion $85.00', price: 85, paypalLink: 'https://www.paypal.com/ncp/payment/Q29LYT24MBCDE' },
  { value: '7k_first_basic_signup', label: '7k First Basic Signup $159.00', price: 159, paypalLink: 'https://www.paypal.com/ncp/payment/Q6JBZNAAYLBYN' },
  { value: '7k_basic_pro', label: '7k Basic +Pro (First Month) $220.00', price: 220, paypalLink: 'https://www.paypal.com/ncp/payment/6D6TC29UF6WSW' },
  { value: '7k_basic_standard_saver', label: '7k Basic +Standard Saver (First Month) $280.00', price: 280, paypalLink: 'https://www.paypal.com/ncp/payment/GALV7JDC38J48' },
  { value: '7k_basic_premium_saver', label: '7k Basic +Premium Saver (First Month) $350.00', price: 350, paypalLink: 'https://www.paypal.com/ncp/payment/RFX9WKPRL35GS' },
  { value: '7k_legacy_pro', label: '7k Legacy +Pro (First Month) $690.00', price: 690, paypalLink: 'https://www.paypal.com/ncp/payment/RCP6NR97GSQ28' },
  { value: '7k_legacy_standard_saver', label: '7k Legacy +Standard Saver (First Month) $750.00', price: 750, paypalLink: 'https://www.paypal.com/ncp/payment/26M54NEZKMDZN' },
  { value: '7k_legacy_premium_saver', label: '7k Legacy +Premium Saver (First Month) $820.00', price: 820, paypalLink: 'https://www.paypal.com/ncp/payment/E53QC5JGVWMNL' },
];

// Default PayPal configuration
const PAYPAL_DEFAULT_LINK = 'https://www.paypal.com/ncp/payment/RFX9WKPRL35GS';
const PAYPAL_EMAIL = 'tarajomil@me.com';

const HindiForm = () => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  
  const [formData, setFormData] = useState({
    fullName: '',
    dateOfBirth: '',
    email: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    password: '',
    confirmPassword: '',
    sponsorName: '',
    membershipTier: '',
    signatureDate: new Date().toISOString().split('T')[0],
    isHumanVerified: false,
  });
  
  const [idDocument, setIdDocument] = useState(null);
  const [proofOfAddress, setProofOfAddress] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Check if passwords match (for real-time highlighting)
  const passwordsMatch = formData.password === formData.confirmPassword || formData.confirmPassword === '';

  // Get selected membership price
  const getSelectedPrice = () => {
    const tier = MEMBERSHIP_TIERS.find(t => t.value === formData.membershipTier);
    return tier ? tier.price : 0;
  };

  const getSelectedTierLabel = () => {
    const tier = MEMBERSHIP_TIERS.find(t => t.value === formData.membershipTier);
    return tier ? tier.label : '';
  };

  // Get PayPal link for selected tier
  const getSelectedPayPalLink = () => {
    const tier = MEMBERSHIP_TIERS.find(t => t.value === formData.membershipTier);
    return tier ? tier.paypalLink : PAYPAL_DEFAULT_LINK;
  };

  // Handle PayPal checkout
  const handlePayPalCheckout = () => {
    const price = getSelectedPrice();
    if (price > 0) {
      // Open tier-specific PayPal payment link
      window.open(getSelectedPayPalLink(), '_blank');
    } else {
      toast.error('Please select a membership tier first');
    }
  };

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = () => {
        if (type === 'id') {
          setIdDocument({ name: file.name, data: reader.result });
        } else {
          setProofOfAddress({ name: file.name, data: reader.result });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Signature Canvas Functions
  const getCoordinates = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e, canvas);
    
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e, canvas);
    
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const getSignatureData = () => {
    const canvas = canvasRef.current;
    return canvas.toDataURL('image/png');
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Personal Information
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    
    // Address
    if (!formData.addressLine1.trim()) newErrors.addressLine1 = 'Address Line 1 is required';
    if (!formData.addressLine2.trim()) newErrors.addressLine2 = 'Address Line 2 is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.postalCode.trim()) newErrors.postalCode = 'Postal code is required';
    if (!formData.country.trim()) newErrors.country = 'Country is required';
    
    // Account Security
    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    if (!formData.confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
    else if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    
    // Sponsor/Referral
    if (!formData.sponsorName.trim()) newErrors.sponsorName = 'Sponsor/Referral name is required';
    
    // Document Uploads
    if (!idDocument) newErrors.idDocument = 'ID document is required';
    if (!proofOfAddress) newErrors.proofOfAddress = 'Proof of address is required';
    
    // Membership
    if (!formData.membershipTier) newErrors.membershipTier = 'Please select a membership tier';
    
    // Signature & Verification
    if (!hasSignature) newErrors.signature = 'Signature is required';
    if (!formData.isHumanVerified) newErrors.isHumanVerified = 'Please verify you are human';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      // Scroll to first error
      const firstError = document.querySelector('.error-message');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        full_name: formData.fullName,
        date_of_birth: formData.dateOfBirth,
        email: formData.email,
        phone: formData.phone,
        address_line1: formData.addressLine1,
        address_line2: formData.addressLine2,
        city: formData.city,
        state: formData.state,
        postal_code: formData.postalCode,
        country: formData.country,
        password: formData.password,
        sponsor_name: formData.sponsorName,
        id_document: idDocument.data,
        id_document_name: idDocument.name,
        proof_of_address: proofOfAddress.data,
        proof_of_address_name: proofOfAddress.name,
        membership_tier: formData.membershipTier,
        signature: getSignatureData(),
        signature_date: formData.signatureDate,
        is_human_verified: formData.isHumanVerified,
        agreed_to_terms: true,
        source: 'hindi-form'
      };

      await axios.post(`${API}/membership/apply`, payload);

      toast.success('Application submitted successfully! Please complete payment via PayPal.');
      
      // Show PayPal section instead of resetting
      setFormSubmitted(true);
      
    } catch (error) {
      console.error('Form submission error:', error);
      const errorMessage = error.response?.data?.detail || 'An error occurred. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle = {
    background: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(212, 175, 55, 0.3)',
    color: '#fff',
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '16px',
    width: '100%',
  };

  const labelStyle = {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: '14px',
    fontWeight: '500',
    marginBottom: '6px',
    display: 'block',
  };

  const sectionStyle = {
    marginBottom: '2rem',
    paddingBottom: '1.5rem',
    borderBottom: '1px solid rgba(212, 175, 55, 0.2)',
  };

  const sectionTitleStyle = {
    color: '#D4AF37',
    fontSize: '1.2rem',
    fontWeight: '600',
    marginBottom: '1.25rem',
  };

  return (
    <div className="landing-page" style={{ minHeight: '100vh' }}>
      {/* Header */}
      <header className="header">
        <div className="header-container">
          <div className="logo">
            <img src="/epg_logo.png" alt="EPG Logo" className="logo-icon" />
            <span className="logo-text">7k Membership</span>
          </div>
        </div>
      </header>

      {/* Form Section */}
      <section className="contact-section" style={{ paddingTop: '100px', paddingBottom: '60px' }}>
        <div className="contact-container" style={{ maxWidth: '800px' }}>
          <h1 className="contact-title" style={{ fontSize: '1.8rem', marginBottom: '0.5rem' }}>
            7k India- Membership Intake & Payment (USD)
          </h1>
          <p style={{ 
            color: 'rgba(255, 255, 255, 0.8)', 
            textAlign: 'center',
            marginBottom: '2rem',
            fontSize: '1rem'
          }}>
            Complete the form below to join 7k
          </p>

          <Card className="contact-card" style={{ 
            background: 'rgba(0, 0, 0, 0.5)', 
            border: '1px solid rgba(212, 175, 55, 0.3)',
            backdropFilter: 'blur(10px)',
          }}>
            <CardContent style={{ padding: '2rem' }}>
              {formSubmitted ? (
                /* PayPal Payment Section - Shown after form submission */
                <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                  <div style={{ 
                    width: '80px', 
                    height: '80px', 
                    background: 'linear-gradient(135deg, #D4AF37 0%, #F4D03F 100%)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1.5rem',
                  }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                  
                  <h2 style={{ color: '#D4AF37', fontSize: '1.5rem', marginBottom: '1rem' }}>
                    Application Submitted Successfully!
                  </h2>
                  
                  <p style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '2rem', lineHeight: '1.6' }}>
                    Thank you for your application. Please complete your payment via PayPal to finalize your membership.
                  </p>
                  
                  <div style={{
                    background: 'rgba(212, 175, 55, 0.1)',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    marginBottom: '2rem',
                  }}>
                    <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                      Selected Membership:
                    </p>
                    <p style={{ color: '#D4AF37', fontSize: '1.2rem', fontWeight: '600', marginBottom: '1rem' }}>
                      {getSelectedTierLabel()}
                    </p>
                    <p style={{ color: '#fff', fontSize: '2rem', fontWeight: '700' }}>
                      ${getSelectedPrice()}.00 USD
                    </p>
                  </div>
                  
                  <Button
                    type="button"
                    onClick={handlePayPalCheckout}
                    style={{
                      width: '100%',
                      background: '#0070ba',
                      color: '#fff',
                      fontWeight: '600',
                      padding: '1rem 2rem',
                      fontSize: '1.1rem',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.75rem',
                      marginBottom: '1rem',
                    }}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.59 3.025-2.566 6.082-8.558 6.082h-2.19c-1.717 0-3.146 1.27-3.407 2.939l-1.138 7.212a.641.641 0 0 0 .633.74h3.554l.795-5.045a1.5 1.5 0 0 1 1.479-1.271h.957c4.617 0 8.214-1.872 9.27-7.297.253-1.303.164-2.403-.747-3.073z"/>
                    </svg>
                    Pay with PayPal
                  </Button>
                  
                  <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.85rem' }}>
                    You will be redirected to PayPal to complete your payment securely.
                  </p>
                  
                  <Button
                    type="button"
                    onClick={() => setFormSubmitted(false)}
                    style={{
                      width: '100%',
                      background: 'transparent',
                      border: '1px solid rgba(212, 175, 55, 0.5)',
                      color: '#D4AF37',
                      fontWeight: '500',
                      padding: '0.75rem 2rem',
                      fontSize: '0.95rem',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      marginTop: '0.5rem',
                    }}
                  >
                    ← Go Back to Edit Form
                  </Button>
                  
                  <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(212, 175, 55, 0.2)' }}>
                    <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.9rem' }}>
                      Need help? Contact us at{' '}
                      <a href="mailto:electrumpg@gmail.com" style={{ color: '#D4AF37' }}>electrumpg@gmail.com</a>
                    </p>
                  </div>
                </div>
              ) : (
              <form onSubmit={handleSubmit}>
                
                {/* Personal Information Section */}
                <div style={sectionStyle}>
                  <h3 style={sectionTitleStyle}>Personal Information</h3>
                  
                  <div style={{ marginBottom: '1rem' }}>
                    <Label style={labelStyle}>Full Name *</Label>
                    <Input
                      type="text"
                      name="fullName"
                      placeholder="Enter your full name"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      style={inputStyle}
                    />
                    {errors.fullName && <span className="error-message" style={{ color: '#ff6b6b', fontSize: '12px' }}>{errors.fullName}</span>}
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <Label style={labelStyle}>Date of Birth *</Label>
                    <Input
                      type="date"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      style={{...inputStyle, colorScheme: 'dark'}}
                    />
                    {errors.dateOfBirth && <span className="error-message" style={{ color: '#ff6b6b', fontSize: '12px' }}>{errors.dateOfBirth}</span>}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    <div>
                      <Label style={labelStyle}>Email Address *</Label>
                      <Input
                        type="email"
                        name="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        style={inputStyle}
                      />
                      {errors.email && <span className="error-message" style={{ color: '#ff6b6b', fontSize: '12px' }}>{errors.email}</span>}
                    </div>
                    <div>
                      <Label style={labelStyle}>Phone Number *</Label>
                      <Input
                        type="tel"
                        name="phone"
                        placeholder="+1 234 567 8900"
                        value={formData.phone}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        style={inputStyle}
                      />
                      {errors.phone && <span className="error-message" style={{ color: '#ff6b6b', fontSize: '12px' }}>{errors.phone}</span>}
                    </div>
                  </div>
                </div>

                {/* Address Section */}
                <div style={sectionStyle}>
                  <h3 style={sectionTitleStyle}>Address</h3>
                  
                  <div style={{ marginBottom: '1rem' }}>
                    <Label style={labelStyle}>Address Line 1 *</Label>
                    <Input
                      type="text"
                      name="addressLine1"
                      placeholder="Street address"
                      value={formData.addressLine1}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      style={inputStyle}
                    />
                    {errors.addressLine1 && <span className="error-message" style={{ color: '#ff6b6b', fontSize: '12px' }}>{errors.addressLine1}</span>}
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <Label style={labelStyle}>Address Line 2 *</Label>
                    <Input
                      type="text"
                      name="addressLine2"
                      placeholder="Apartment, suite, etc."
                      value={formData.addressLine2}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      style={inputStyle}
                    />
                    {errors.addressLine2 && <span className="error-message" style={{ color: '#ff6b6b', fontSize: '12px' }}>{errors.addressLine2}</span>}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    <div>
                      <Label style={labelStyle}>City *</Label>
                      <Input
                        type="text"
                        name="city"
                        placeholder="City"
                        value={formData.city}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        style={inputStyle}
                      />
                      {errors.city && <span className="error-message" style={{ color: '#ff6b6b', fontSize: '12px' }}>{errors.city}</span>}
                    </div>
                    <div>
                      <Label style={labelStyle}>State/Province *</Label>
                      <Input
                        type="text"
                        name="state"
                        placeholder="State/Province"
                        value={formData.state}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        style={inputStyle}
                      />
                      {errors.state && <span className="error-message" style={{ color: '#ff6b6b', fontSize: '12px' }}>{errors.state}</span>}
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <Label style={labelStyle}>Postal/ZIP Code *</Label>
                      <Input
                        type="text"
                        name="postalCode"
                        placeholder="Postal code"
                        value={formData.postalCode}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        style={inputStyle}
                      />
                      {errors.postalCode && <span className="error-message" style={{ color: '#ff6b6b', fontSize: '12px' }}>{errors.postalCode}</span>}
                    </div>
                    <div>
                      <Label style={labelStyle}>Country *</Label>
                      <Input
                        type="text"
                        name="country"
                        placeholder="Country"
                        value={formData.country}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        style={inputStyle}
                      />
                      {errors.country && <span className="error-message" style={{ color: '#ff6b6b', fontSize: '12px' }}>{errors.country}</span>}
                    </div>
                  </div>
                </div>

                {/* Account Security Section */}
                <div style={sectionStyle}>
                  <h3 style={sectionTitleStyle}>Account Security</h3>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <Label style={labelStyle}>Password *</Label>
                      <div style={{ position: 'relative' }}>
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          placeholder="Create a password"
                          value={formData.password}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                          style={{
                            ...inputStyle,
                            paddingRight: '45px',
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{
                            position: 'absolute',
                            right: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {showPassword ? (
                            <EyeOff size={20} color="rgba(212, 175, 55, 0.7)" />
                          ) : (
                            <Eye size={20} color="rgba(212, 175, 55, 0.7)" />
                          )}
                        </button>
                      </div>
                      {errors.password && <span className="error-message" style={{ color: '#ff6b6b', fontSize: '12px' }}>{errors.password}</span>}
                    </div>
                    <div>
                      <Label style={labelStyle}>Confirm Password *</Label>
                      <div style={{ position: 'relative' }}>
                        <Input
                          type={showConfirmPassword ? 'text' : 'password'}
                          name="confirmPassword"
                          placeholder="Confirm your password"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                          style={{
                            ...inputStyle,
                            paddingRight: '45px',
                            borderColor: !passwordsMatch ? '#ff6b6b' : 'rgba(212, 175, 55, 0.3)',
                            boxShadow: !passwordsMatch ? '0 0 0 1px #ff6b6b' : 'none',
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          style={{
                            position: 'absolute',
                            right: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {showConfirmPassword ? (
                            <EyeOff size={20} color="rgba(212, 175, 55, 0.7)" />
                          ) : (
                            <Eye size={20} color="rgba(212, 175, 55, 0.7)" />
                          )}
                        </button>
                      </div>
                      {!passwordsMatch && formData.confirmPassword && (
                        <span className="error-message" style={{ color: '#ff6b6b', fontSize: '12px' }}>Passwords do not match</span>
                      )}
                      {errors.confirmPassword && passwordsMatch && <span className="error-message" style={{ color: '#ff6b6b', fontSize: '12px' }}>{errors.confirmPassword}</span>}
                    </div>
                  </div>
                </div>

                {/* Sponsor/Referral Section */}
                <div style={sectionStyle}>
                  <h3 style={sectionTitleStyle}>Sponsor / Referral</h3>
                  
                  <div>
                    <Label style={labelStyle}>Sponsor/Referral Name *</Label>
                    <Input
                      type="text"
                      name="sponsorName"
                      placeholder="Name of person who referred you"
                      value={formData.sponsorName}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      style={inputStyle}
                    />
                    {errors.sponsorName && <span className="error-message" style={{ color: '#ff6b6b', fontSize: '12px' }}>{errors.sponsorName}</span>}
                  </div>
                </div>

                {/* Document Upload Section */}
                <div style={sectionStyle}>
                  <h3 style={sectionTitleStyle}>Document Upload</h3>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <Label style={labelStyle}>Upload ID *</Label>
                      <div style={{
                        border: errors.idDocument ? '2px dashed #ff6b6b' : '2px dashed rgba(212, 175, 55, 0.4)',
                        borderRadius: '8px',
                        padding: '1.5rem',
                        textAlign: 'center',
                        cursor: 'pointer',
                        background: 'rgba(255, 255, 255, 0.05)',
                        transition: 'all 0.3s ease',
                      }}>
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => handleFileChange(e, 'id')}
                          disabled={isSubmitting}
                          style={{ display: 'none' }}
                          id="idUpload"
                        />
                        <label htmlFor="idUpload" style={{ cursor: 'pointer', display: 'block' }}>
                          {idDocument ? (
                            <span style={{ color: '#D4AF37' }}>{idDocument.name}</span>
                          ) : (
                            <>
                              <span style={{ color: 'rgba(255,255,255,0.6)', display: 'block' }}>Click to upload</span>
                              <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>Max 5MB</span>
                            </>
                          )}
                        </label>
                      </div>
                      {errors.idDocument && <span className="error-message" style={{ color: '#ff6b6b', fontSize: '12px' }}>{errors.idDocument}</span>}
                    </div>
                    <div>
                      <Label style={labelStyle}>Upload Proof of Address *</Label>
                      <div style={{
                        border: errors.proofOfAddress ? '2px dashed #ff6b6b' : '2px dashed rgba(212, 175, 55, 0.4)',
                        borderRadius: '8px',
                        padding: '1.5rem',
                        textAlign: 'center',
                        cursor: 'pointer',
                        background: 'rgba(255, 255, 255, 0.05)',
                        transition: 'all 0.3s ease',
                      }}>
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => handleFileChange(e, 'address')}
                          disabled={isSubmitting}
                          style={{ display: 'none' }}
                          id="addressUpload"
                        />
                        <label htmlFor="addressUpload" style={{ cursor: 'pointer', display: 'block' }}>
                          {proofOfAddress ? (
                            <span style={{ color: '#D4AF37' }}>{proofOfAddress.name}</span>
                          ) : (
                            <>
                              <span style={{ color: 'rgba(255,255,255,0.6)', display: 'block' }}>Click to upload</span>
                              <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>Max 5MB</span>
                            </>
                          )}
                        </label>
                      </div>
                      {errors.proofOfAddress && <span className="error-message" style={{ color: '#ff6b6b', fontSize: '12px' }}>{errors.proofOfAddress}</span>}
                    </div>
                  </div>
                </div>

                {/* Membership Selection Section */}
                <div style={sectionStyle}>
                  <h3 style={sectionTitleStyle}>Membership Tier Selection *</h3>
                  
                  <Select 
                    value={formData.membershipTier} 
                    onValueChange={(value) => handleSelectChange('membershipTier', value)}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger style={{
                      ...inputStyle,
                      height: '48px',
                    }}>
                      <SelectValue placeholder="Select your membership tier" />
                    </SelectTrigger>
                    <SelectContent style={{ 
                      background: '#1a1a2e', 
                      border: '1px solid rgba(212, 175, 55, 0.3)',
                      zIndex: 9999,
                    }}>
                      {MEMBERSHIP_TIERS.map(tier => (
                        <SelectItem 
                          key={tier.value} 
                          value={tier.value}
                          style={{ color: '#fff', cursor: 'pointer' }}
                        >
                          {tier.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.membershipTier && <span className="error-message" style={{ color: '#ff6b6b', fontSize: '12px' }}>{errors.membershipTier}</span>}
                </div>

                {/* Signature Section */}
                <div style={sectionStyle}>
                  <h3 style={sectionTitleStyle}>Signature & Verification</h3>
                  
                  <div style={{ marginBottom: '1.5rem' }}>
                    <Label style={labelStyle}>Signature *</Label>
                    <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginBottom: '0.75rem' }}>
                      Please sign in the box below using your mouse or finger
                    </p>
                    <div style={{ 
                      background: '#fff', 
                      borderRadius: '8px', 
                      padding: '4px',
                      border: errors.signature ? '2px solid #ff6b6b' : '2px solid rgba(212, 175, 55, 0.5)',
                    }}>
                      <canvas
                        ref={canvasRef}
                        width={600}
                        height={150}
                        style={{ 
                          width: '100%', 
                          height: '150px', 
                          cursor: 'crosshair',
                          touchAction: 'none',
                          borderRadius: '6px',
                        }}
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={clearSignature}
                      disabled={isSubmitting}
                      style={{
                        marginTop: '0.5rem',
                        background: 'transparent',
                        border: '1px solid rgba(212, 175, 55, 0.5)',
                        color: '#D4AF37',
                        padding: '0.5rem 1rem',
                        fontSize: '13px',
                      }}
                    >
                      Clear Signature
                    </Button>
                    {errors.signature && <span className="error-message" style={{ color: '#ff6b6b', fontSize: '12px', display: 'block', marginTop: '0.5rem' }}>{errors.signature}</span>}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                    <div>
                      <Label style={labelStyle}>Date *</Label>
                      <Input
                        type="date"
                        name="signatureDate"
                        value={formData.signatureDate}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        style={{...inputStyle, colorScheme: 'dark'}}
                      />
                    </div>
                  </div>

                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '0.75rem',
                    padding: '1rem',
                    background: 'rgba(212, 175, 55, 0.1)',
                    borderRadius: '8px',
                    border: errors.isHumanVerified ? '1px solid #ff6b6b' : '1px solid rgba(212, 175, 55, 0.2)',
                  }}>
                    <Checkbox
                      id="humanVerify"
                      checked={formData.isHumanVerified}
                      onCheckedChange={(checked) => handleCheckboxChange('isHumanVerified', checked)}
                      disabled={isSubmitting}
                      style={{ marginTop: '2px' }}
                    />
                    <label htmlFor="humanVerify" style={{ 
                      color: 'rgba(255, 255, 255, 0.9)', 
                      fontSize: '14px', 
                      lineHeight: '1.5',
                      cursor: 'pointer',
                    }}>
                      Please verify that you are human *
                    </label>
                  </div>
                  {errors.isHumanVerified && <span className="error-message" style={{ color: '#ff6b6b', fontSize: '12px' }}>{errors.isHumanVerified}</span>}
                </div>

                {/* Submit Button */}
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  style={{ 
                    width: '100%',
                    background: 'linear-gradient(135deg, #D4AF37 0%, #F4D03F 100%)',
                    color: '#000',
                    fontWeight: '600',
                    padding: '1rem',
                    fontSize: '1.1rem',
                    borderRadius: '8px',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    opacity: isSubmitting ? 0.7 : 1,
                  }}
                >
                  {isSubmitting ? 'Processing...' : 'Continue to Payment'}
                </Button>

                <p style={{ 
                  textAlign: 'center', 
                  color: 'rgba(255, 255, 255, 0.5)', 
                  fontSize: '12px',
                  marginTop: '1rem',
                }}>
                  By submitting this form, you agree to our{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#D4AF37' }}>
                    Privacy Policy
                  </a>
                </p>
              </form>
              )}
            </CardContent>
          </Card>

          <div style={{ 
            marginTop: '2rem', 
            textAlign: 'center',
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: '0.9rem'
          }}>
            <p>Questions? Email us at: <a href="mailto:electrumpg@gmail.com" style={{ color: '#D4AF37' }}>electrumpg@gmail.com</a></p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HindiForm;
